import _ from 'lodash';
import {useMemo} from 'react';

import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {ChannelTypesEnum} from '../../models/api/channelCommonTypes';
import {ChannelModel} from '../../models/api/channelModel';
import {ApiSmtpDomains} from '../../models/api/smtpDomainModel';
import {fetchApiAsync, fetchAsync} from '../../services/http/http';
import {useSettingsQuery, UseSettingsQueryOptions} from '../helpers/useSettingsQuery';

/*
 * Consts.
 */

const dnsProviderQueryKeyFactory = {
  id: (channelId: number) => ['dns_providers_by_domain', {id: channelId}],
};

const dnsRecordsQueryKeyFactory = {
  id: (channelId: number, showPrevious: boolean) => ['dns_records', {id: channelId, showPrevious}],
};

/*
 * Fetchers.
 */

export function useFetchDnsProvidersbyDomain(channel: ChannelModel, options?: UseSettingsQueryOptions) {
  const {data: dnsProvidersByDomain, asyncStatus} = useSettingsQuery(
    dnsProviderQueryKeyFactory.id(channel.id),
    async ({company}) => {
      const baseData = await fetchApiAsync(ApiSmtpDomains, `${company.url}/customer_domains`);
      return _(baseData)
        .keyBy((d) => d.name)
        .mapValues((d) => d.dnsProvider)
        .value();
    },
    options,
  );

  const dnsProvider = useMemo(() => {
    if (
      asyncStatus === AsyncStatusesEnum.LOADED &&
      dnsProvidersByDomain &&
      channel.type === ChannelTypesEnum.SMTP &&
      channel.sendAsDomain
    ) {
      return dnsProvidersByDomain[channel.sendAsDomain];
    }

    return '';
  }, [asyncStatus, channel, dnsProvidersByDomain]);

  return {dnsProvidersByDomain, dnsProvider, status: asyncStatus};
}

export function useFetchDnsRecords(
  channel: ChannelModel,
  showPrevious: boolean,
  options?: UseSettingsQueryOptions,
) {
  const {data: dnsRecords} = useSettingsQuery(
    dnsRecordsQueryKeyFactory.id(channel.id, showPrevious),
    async () => {
      const baseData = await fetchAsync(`${channel.url}/view_smtp_dns?subuser_transition=${showPrevious}`);

      return _(baseData)
        .omit('domain', 'valid')
        .map((v) => ({
          name: v.name,
          type: String(v.type).toUpperCase(),
          value: v.value,
        }))
        .value();
    },
    {...options, refetchOnWindowFocus: false, refetchOnReconnect: false, refetchOnMount: false},
  );

  return {dnsRecords};
}
